import type {
  ICRUDFormDiMetadata,
  IFormMetadataByStep,
  IFormMetadataByStepResponse,
  IUpdateWorkflowScreensResponse
} from "@kuika/kuika-cl-model";
import type { AxiosResponse } from "axios";
import type { IScreenDesignContextState } from "../../contexts/screen-design-context/screen-design-context";
import { WorkflowScreenService } from "../../services/workflow-screen-service";
import type { ScreenDesignInstance } from "./designer-context";
import { tryResolveEditTarget } from "./detached-screen-session";
import { findManagedScreenIdForStep } from "./managed-task-screen";
import type { CompositionOperation } from "./screen-composition-ledger";
import { buildCompositionPayload, type IBuildCompositionPayloadResult } from "./screen-composition-payload";

/**
 * SENDING what was composed on a task screen — the last link of the screen-builder chain.
 *
 * ## The chain, and why this is its own step
 *
 * Everything before it writes only the browser's own copy of the screen:
 *
 *   `openDetachedScreenSession` → `placeComponent` / `placeChildItem` / `reorderSibling` /
 *   `removeComponent` / the property + action writers with a `screenId` → the ledger
 *
 * None of that reaches the server. A task screen is regenerated by `UpdateWorkflowScreens`, and
 * composition rides that same pass as a list of ADDRESSED operations (see the ledger for why it is
 * operations and not a tree). So a caller that composes and never calls this has done work that
 * evaporates at the next apply — which is exactly what the screen builder's UI did for it, and what
 * nothing did after that UI was removed.
 *
 * ## The metadata is re-read, never invented
 *
 * The request is per STEP and its `formMetadata` is what the server regenerates the form from, so a
 * composition call has to carry some. It carries the server's OWN current metadata, read back
 * immediately before the send and passed through untouched: composition must not move a field, a
 * section or a layout as a side effect of adding a button. If that read fails the call is refused
 * rather than sent with a guess — a request with the wrong metadata would rebuild the form from it.
 *
 * ## Scope
 *
 * `onlyProvidedSteps` is set for the same reason the assign path and the old UI set it: without it
 * the platform reprocesses every managed screen in the project, which is slow, briefly breaks
 * unrelated screens, and has historically pushed this call past its caller's timeout.
 */

export interface IApplyScreenCompositionInput {
  /** The workflow (BPMN process) the step belongs to. */
  processId: string;
  /** The user-task step whose managed screen was composed. */
  stepId: string;
  /** The operations staged for that step, in the order they were made. */
  operations: readonly CompositionOperation[];
}

export interface IApplyScreenCompositionResult {
  isSuccess: boolean;
  /** How many operations reached the server. */
  sentCount: number;
  /**
   * Operations dropped before the send because their target is no longer on the screen.
   *
   * Reported rather than hidden: the caller staged them, and silently sending fewer is how a user
   * is told the work landed when part of it did not.
   */
  droppedCount: number;
  /** Why it did not succeed — the server's own per-step reason when it refused. */
  message?: string;
}

const refuse = (message: string, droppedCount: number = 0): IApplyScreenCompositionResult => ({
  isSuccess: false,
  sentCount: 0,
  droppedCount,
  message
});

/**
 * Send one step's staged composition to the server, and report what the server said about it.
 *
 * Staying silent on an empty ledger is deliberate: a caller that composed nothing must not trigger
 * a screen regeneration, because a regeneration is not free (it rewrites the form and the decision
 * buttons on the server's copy).
 */
export async function applyScreenComposition(
  sdInstance: ScreenDesignInstance,
  input: IApplyScreenCompositionInput
): Promise<IApplyScreenCompositionResult> {
  if (input.operations.length === 0) {
    return { isSuccess: true, sentCount: 0, droppedCount: 0 };
  }

  const live: IScreenDesignContextState | undefined = tryResolveEditTarget(sdInstance, null);
  const screenId: string | null = findManagedScreenIdForStep(live?.project, input.processId, input.stepId);
  if (screenId === null) {
    return refuse(`Step ${input.stepId} has no platform-managed screen, so there is nothing to compose on.`);
  }

  // The tree the operations were staged against. `tryResolveEditTarget` answers with the open
  // screen when this screen IS the open one, and with the session otherwise — and with nothing when
  // neither is loaded, which means the ids in the ledger cannot be resolved at all.
  const target: IScreenDesignContextState | undefined = tryResolveEditTarget(sdInstance, screenId);
  if (target === undefined) {
    return refuse(
      `Screen "${screenId}" is neither open nor loaded in a detached session, so its composition ` +
        `cannot be built. Open a session for it before composing.`
    );
  }

  const payload: IBuildCompositionPayloadResult = buildCompositionPayload(
    input.operations,
    target.screenDesignDictionary
  );
  if (payload.operations.length === 0) {
    return refuse(
      `None of the ${input.operations.length} staged change(s) could be sent — every target has left the screen.`,
      payload.droppedCount
    );
  }

  const metadataResponse: AxiosResponse<ICRUDFormDiMetadata> = await WorkflowScreenService.GetFormMetadataOfScreen(
    screenId,
    { processId: input.processId, stepId: input.stepId }
  );
  const formMetadata: ICRUDFormDiMetadata | undefined = metadataResponse?.data;
  // `sections` is what the server regenerates the form from; its absence means the read failed
  // (the service swallows transport errors and hands the error object back as the response).
  if (formMetadata?.sections === undefined) {
    return refuse(
      `The form metadata of screen "${screenId}" could not be read, so its composition was not sent ` +
        `(sending without it would rebuild the form from nothing).`,
      payload.droppedCount
    );
  }

  const entry: IFormMetadataByStep = {
    processId: input.processId,
    stepId: input.stepId,
    formMetadata,
    compositionOperations: payload.operations
  };
  const response: AxiosResponse<IUpdateWorkflowScreensResponse> = await WorkflowScreenService.UpdateWorkflowScreens({
    formMetadataBySteps: [entry],
    onlyProvidedSteps: true
  });

  // A refusal is reported PER STEP on a 200 response — this is not an exception path. Reading the
  // step's own entry rather than the transport status is what carries the reason back.
  const stepResult: IFormMetadataByStepResponse | undefined = response?.data?.formMetadataBySteps?.find(
    (candidate: IFormMetadataByStepResponse): boolean => candidate?.stepId === input.stepId
  );
  if (stepResult === undefined) {
    return refuse(
      `The server did not answer about step ${input.stepId}, so whether its composition landed is unknown.`,
      payload.droppedCount
    );
  }
  if (!stepResult.isSuccess) {
    return refuse(stepResult.message ?? "The server refused this step's composition.", payload.droppedCount);
  }

  return {
    isSuccess: true,
    sentCount: payload.operations.length,
    droppedCount: payload.droppedCount
  };
}
